/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

export type BaremetalStatus = 'pending' | 'paid' | 'provisioning' | 'active' | 'released' | 'cancelled';

export interface BaremetalSpec {
  cpu: number;
  ram: number;
  storage: number;
  storage_type: 'SSD' | 'NVMe' | 'HDD';
  gpu?: string;
  gpu_count?: number;
  location: string;
  os: string;
  duration: number; // months
}

export interface BaremetalPricing {
  monthly_price_inr: number;
  total_price_inr: number;
}

export const STORAGE_TYPES = [
  { value: 'NVMe', label: 'NVMe (Fastest)' },
  { value: 'SSD', label: 'SSD (Fast)' },
  { value: 'HDD', label: 'HDD (Economical)' },
];

export const GPU_OPTIONS = [
  { value: '', label: 'No GPU' },
  { value: 'NVIDIA A100', label: 'NVIDIA A100' },
  { value: 'NVIDIA H100', label: 'NVIDIA H100' },
  { value: 'NVIDIA L40S', label: 'NVIDIA L40S' },
  { value: 'NVIDIA RTX 4090', label: 'NVIDIA RTX 4090' },
  { value: 'NVIDIA RTX 3090', label: 'NVIDIA RTX 3090' },
];

export const LOCATIONS = [
  { value: 'Mumbai', label: 'Mumbai, India' },
  { value: 'Delhi', label: 'Delhi, India' },
  { value: 'Bangalore', label: 'Bangalore, India' },
  { value: 'Hyderabad', label: 'Hyderabad, India' },
];

export const OS_OPTIONS = [
  { value: 'Ubuntu 22.04', label: 'Ubuntu 22.04 LTS' },
  { value: 'Ubuntu 20.04', label: 'Ubuntu 20.04 LTS' },
  { value: 'Debian 12', label: 'Debian 12' },
  { value: 'CentOS 8', label: 'CentOS 8' },
  { value: 'Rocky Linux 9', label: 'Rocky Linux 9' },
  { value: 'Windows Server 2022', label: 'Windows Server 2022' },
];

export const DURATION_OPTIONS = [
  { value: 1, label: '1 Month' },
  { value: 3, label: '3 Months (Save 10%)' },
  { value: 6, label: '6 Months (Save 15%)' },
  { value: 12, label: '12 Months (Save 20%)' },
];

// Pricing calculator (matches backend logic)
export const calculateBaremetalPricing = (spec: BaremetalSpec): BaremetalPricing => {
  const cpuPrice = spec.cpu * 500;    // ₹500 per core per month
  const ramPrice = spec.ram * 200;    // ₹200 per GB per month
  const storagePrice = spec.storage * 50; // ₹50 per GB per month
  const gpuPrice = spec.gpu ? (spec.gpu_count || 0) * 10000 : 0; // ₹10,000 per GPU per month

  const monthlyPrice = cpuPrice + ramPrice + storagePrice + gpuPrice;
  const totalPrice = monthlyPrice * spec.duration;

  return {
    monthly_price_inr: monthlyPrice,
    total_price_inr: totalPrice,
  };
};
